import { FileWarning } from 'lucide-react';
import type { ReactElement } from 'react';

import { cn } from '@/components/ui/cn';
import { useActiveCompany } from '@/lib/active-company-context';

import { competencyBadge } from '../presentation';
import { useReaderCompetencies } from '../use-training';
import { skeletonLine } from '@/components/ui/skeleton';

/** Competencias del LMS de una persona, acotadas a sus puestos en la empresa activa.
 *  Se piden al desplegar la persona (no por cada fila). */
export function CompetencyList({
  entryId,
  lmsUserId,
  id,
}: {
  entryId: string;
  lmsUserId: number;
  id: string;
}): ReactElement {
  const { activeCompanyId } = useActiveCompany();
  const query = useReaderCompetencies(entryId, lmsUserId, {
    companyId: activeCompanyId,
    enabled: Boolean(activeCompanyId),
  });

  let body: ReactElement;
  if (query.isLoading) {
    body = (
      <div aria-busy="true" aria-label="Cargando competencias">
        <div className={skeletonLine({ width: 70 })} />
        <div className={skeletonLine({ width: 50, space: 'top' })} />
      </div>
    );
  } else if (query.isError) {
    body = (
      <div className="comp" role="alert">
        <FileWarning aria-hidden="true" size={14} style={{ color: 'var(--danger)' }} />
        <span>No se pudieron cargar las competencias.</span>
        <button
          type="button"
          className="btn mut sm"
          style={{ marginLeft: 'auto' }}
          onClick={() => void query.refetch()}
        >
          Reintentar
        </button>
      </div>
    );
  } else if ((query.data?.competencies ?? []).length === 0) {
    body = <p className="meta">Sin competencias registradas para sus puestos en esta empresa.</p>;
  } else {
    body = (
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'contents' }}>
        {(query.data?.competencies ?? []).map((c) => {
          const b = competencyBadge(c.status);
          return (
            <li key={`${c.position}-${c.name}`} className="comp">
              <div style={{ minWidth: 0 }}>
                <div>{c.name}</div>
                <div className="meta">
                  {c.position} · {c.category} · {c.required ? 'requerida' : 'opcional'}
                </div>
              </div>
              <span className={cn('badge', b.cls)}>{b.label}</span>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div className="comp-list" id={id}>
      {body}
    </div>
  );
}
